import {
  Alert,
  Divider,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  useDisclosure,
} from "@heroui/react";
import { SiDiscord, SiMatrix } from "@icons-pack/react-simple-icons";
import { useCallback, useState } from "react";

import CinnyLogo from "@/assets/cinny.svg";

import { useDialog } from "../../contexts/DialogContext";
import {
  DISCORD_INVITE,
  MATRIX_ALIAS,
  MATRIX_INVITE,
  MATRIX_URL,
} from "../../lib/constants";
import { Button } from "../core/Button";

export const ChatInfoDialog = () => {
  const { state, dispatch } = useDialog();
  const { isOpen, onClose } = useDisclosure({
    isOpen: "CHAT_INFO" in state.dialogs,
    onClose: () => dispatch({ action: "CLOSE_DIALOG", dialogId: "CHAT_INFO" }),
  });
  const [step, setStep] = useState<"WELCOME" | "MATRIX">("WELCOME");
  const [legacyElementDialog, setLegacyElementDialog] = useState(false);

  const WelcomeStep = useCallback(
    ({ openLegacy }: { openLegacy: () => unknown }) => (
      <>
        <div className="flex flex-wrap gap-2 justify-center">
          <p>
            The Canvas event chat is available a couple different places, take
            your pick!
          </p>
          <div className="flex flex-row justify-center items-center w-full">
            <Button
              as={Link}
              size="lg"
              variant="ghost"
              href="https://chat.fediverse.events/switch/cinny"
              target="_blank"
              aria-label="Open Canvas' Matrix space using Cinny"
            >
              <img src={CinnyLogo} className="h-8" />
              <div className="inline-flex flex-col">
                <span>Matrix</span>
                <span className="text-xs text-default-500">using Cinny</span>
              </div>
            </Button>
          </div>
          <div className="flex flex-row justify-center items-center w-full">
            <div className="text-sm *:text-sm">
              or use{" "}
              <Link href="#" onPress={openLegacy}>
                the legacy Element
              </Link>
            </div>
          </div>
          <Divider />
          <span className="text-center">Also available via...</span>

          <div className="flex flex-row justify-center items-center w-full gap-1">
            <Button variant="ghost" onPress={() => setStep("MATRIX")}>
              <SiMatrix size={20} />
              <div className="inline-flex flex-col *:text-left">
                <span>Matrix</span>
                <span className="text-xs text-default-500">{MATRIX_ALIAS}</span>
              </div>
            </Button>
            <Button
              as={Link}
              href={DISCORD_INVITE}
              target="_blank"
              variant="ghost"
            >
              <SiDiscord size={20} />
              Discord
            </Button>
          </div>

          <span className="text-center text-xs *:text-xs">
            Canvas DMs will be directed towards your <code>fediverse.ooo</code>{" "}
            Matrix accounts at this time{" "}
            <Link
              href="https://sc07.dev/sc07/canvas/-/work_items/229"
              target="_blank"
            >
              (tracked #229)
            </Link>
          </span>
        </div>
      </>
    ),
    [],
  );

  const MatrixStep = useCallback(
    () => (
      <>
        <p>
          Canvas supplies Matrix accounts using <code>fediverse.ooo</code>,
          using your existing Fediverse account. (eg.{" "}
          <code>@grant@grants.cafe</code> becomes{" "}
          <code>@grant=40grants.cafe:fediverse.ooo</code>)
        </p>
        <div className="flex flex-col gap-1">
          <Button
            as={Link}
            size="lg"
            variant="ghost"
            href="https://chat.fediverse.events/switch/cinny"
            target="_blank"
            aria-label="Open Canvas' Matrix space using Cinny"
          >
            <img src={CinnyLogo} className="h-8" />
            <div className="inline-flex flex-col *:text-center">
              <span>Matrix</span>
              <span className="text-xs text-default-500">using Cinny</span>
            </div>
          </Button>
          <div className="flex flex-row gap-1 *:grow flex-wrap">
            <Button
              as={Link}
              href={MATRIX_INVITE}
              target="_blank"
              variant="ghost"
            >
              <div className="inline-flex flex-col *:text-center">
                <span>{MATRIX_ALIAS}</span>
                <span className="text-xs text-default-500">
                  using matrix.to
                </span>
              </div>
            </Button>
            {/* MSC2312 */}
            <Button as={Link} href={MATRIX_URL} target="_blank" variant="ghost">
              <div className="inline-flex flex-col *:text-center">
                <span>{MATRIX_ALIAS}</span>
                <span className="text-xs text-default-500">using matrix:</span>
              </div>
            </Button>
          </div>
        </div>
      </>
    ),
    [],
  );

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} isDismissable placement="center">
        <ModalContent className="canvas-overlay-card">
          {(onClose) => (
            <>
              <ModalHeader>Canvas Chat</ModalHeader>
              <ModalBody>
                {step === "WELCOME" && (
                  <WelcomeStep
                    openLegacy={() => setLegacyElementDialog(true)}
                  />
                )}
                {step === "MATRIX" && <MatrixStep />}
              </ModalBody>
              <ModalFooter>
                <Button onPress={onClose}>Close</Button>
                {step !== "WELCOME" && (
                  <Button onPress={() => setStep("WELCOME")}>Back</Button>
                )}
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
      <Modal
        isOpen={legacyElementDialog}
        onClose={() => setLegacyElementDialog(false)}
        isDismissable
        placement="center"
      >
        <ModalContent className="canvas-overlay-card">
          {(onClose) => (
            <>
              <ModalHeader>Legacy Element Chat</ModalHeader>
              <ModalBody>
                <Alert
                  color="warning"
                  title="Deprecated"
                  description="We are migrating away from the Element client"
                />
                <p>
                  This is given as an option for people who did not setup
                  encryption key backups.{" "}
                  <strong>If you do not set up this,</strong> you will be unable
                  to read encrypted messages (like DMs).
                </p>
                <p>This client option will disappear eventually.</p>
              </ModalBody>
              <ModalFooter>
                <Button onPress={onClose}>Back</Button>
                <Button
                  color="warning"
                  as={Link}
                  href="https://chat.fediverse.events/switch/element"
                  target="_blank"
                >
                  Open Element
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};
